import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ImagePlus, MoveHorizontal } from "lucide-react";

/*
  V2.1 · Aufgabe 3 · TRANSFORMATION · Vorher / Nachher Slider
  - Links: Originalfoto Ist-Zustand
  - Rechts: BERLIN ─ CUBE Vision derselben Perspektive
  - Wenn kein exaktes Nachher-Bild vorhanden ist: Platzhalter mit Hinweis.
  - "Vision-Rendering für diese Perspektive erforderlich."
*/

// Perspektiven-Paare. `before` = echtes Foto vom Breitscheidplatz (04.02.2026),
// `after` = Vision-Rendering aus derselben Perspektive (wartet noch auf 3 Renderings).
const PAIRS = [
  {
    key: "breitscheidplatz-wide",
    label: "Breitscheidplatz · Weite Perspektive",
    before: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/td8tme20_grafik.png",
    after: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/luwd6n2a_ChatGPT%20Image%205.%20Juli%202026%2C%2016_55_29.png",
    afterFocus: "center 42%",
  },
  {
    key: "gedaechtniskirche",
    label: "Gedächtniskirche · Vorplatz",
    before: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/ax9e1xy8_grafik.png",
    after: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/1fm4q4y1_ChatGPT%20Image%205.%20Juli%202026%2C%2017_09_54.png",
    afterFocus: "100% center",
  },
  {
    key: "kantstrasse",
    label: "Kantstraße · Übergang",
    before: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/et4nmnue_image-1783260218962.jpg",
    after: "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/frv0dllz_grafik.png",
    afterFocus: "35% 60%",
    realized: true,
    caption: {
      de: "Links: vor dem Aufbau. Rechts: die realisierte Anlage.",
      en: "Left: before installation. Right: the realised project.",
    },
  },
];

// Placeholder subcomponent — outside slider to avoid re-mounting on every render
function SlotPlaceholder({ kind, upload, label }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6 bg-[#16191D]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: "linear-gradient(45deg, transparent 48%, rgba(255,255,255,.7) 49%, rgba(255,255,255,.7) 51%, transparent 52%)",
          backgroundSize: "20px 20px",
        }}
      />
      <ImagePlus className="h-8 w-8 text-white/40" strokeWidth={1.2} />
      <div className="text-[10px] tracking-[0.35em] uppercase text-white/60 font-display">
        {kind === "before" ? upload.before : upload.after}
      </div>
      <div className="text-[10px] tracking-[0.2em] text-white/40 max-w-[240px] mt-1">
        {label}
      </div>
    </div>
  );
}

// Slider component — 2-image compare with draggable handle
function BeforeAfterSlider({ before, after, afterFocus, upload, label, realized }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      move(cx);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [move]);

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[16/10] border border-white/10 overflow-hidden select-none cursor-ew-resize bg-[#0F1114]"
      onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; move(e.touches[0].clientX); }}
      data-testid={`before-after-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {/* AFTER (Vision) — full width beneath */}
      {after ? (
        <img
          src={after}
          alt={`Visualisierung: ${label} mit Berlin-Cube-Modulen`}
          className="absolute inset-0 w-full h-full object-cover"
          style={afterFocus ? { objectPosition: afterFocus } : undefined}
        />
      ) : (
        <SlotPlaceholder kind="after" upload={upload} label={label} />
      )}

      {/* BEFORE (Today) — clipped by pos% */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        {before ? (
          <img src={before} alt={`${label} · Heute`} className="w-full h-full object-cover" />
        ) : (
          <SlotPlaceholder kind="before" upload={upload} label={label} />
        )}
      </div>

      {/* Labels · Foto ↔ Visualisierung / Realisiert (Trennung Realität / Zukunft) */}
      <div className="absolute top-4 left-4 z-10 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
        Heute · Foto
      </div>
      <div className={`absolute top-4 right-4 z-10 text-[10px] tracking-[0.35em] uppercase bg-black/50 backdrop-blur px-3 py-1.5 border font-display ${realized ? "text-white border-white/25" : "text-[#F5D77A] border-[#F5D77A]/30"}`}>
        {realized ? "Heute · Realisiert" : "Visualisierung"}
      </div>
      {after && (
        <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2 text-[9px] tracking-[0.35em] uppercase text-white/70 bg-black/45 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
          <span className={`h-1.5 w-1.5 rounded-full ${realized ? "bg-white/80" : "bg-[#F5D77A]/80"}`} />
          {realized ? "Foto · Realität" : "Vision · Visualisierung"}
        </div>
      )}

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/90 pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-12 h-12 rounded-full bg-white text-[#1F2328] shadow-2xl flex items-center justify-center border-4 border-[#1F2328] pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal className="h-5 w-5" strokeWidth={2} />
      </div>
    </div>
  );
}

export default function Transformation() {
  const { lang } = useLang();
  const copy = {
    de: {
      kicker: "Akt III — Transformation",
      title: "Vorher. Nachher.",
      sub: "Dieselben Perspektiven. Zwei Realitäten.",
      tagline: "Eine Vision für das Berlin von morgen.",
      hint: "Schieberegler nach links oder rechts ziehen.",
      upload: {
        before: "Originalfoto benötigt",
        after: "Vision-Rendering für diese Perspektive erforderlich",
      },
    },
    en: {
      kicker: "Act III — Transformation",
      title: "Before. After.",
      sub: "Same perspectives. Two realities.",
      tagline: "A vision for the Berlin of tomorrow.",
      hint: "Drag the slider left or right.",
      upload: {
        before: "Original photo required",
        after: "Vision rendering for this perspective required",
      },
    },
  }[lang];

  return (
    <section
      id="transformation"
      data-testid="section-transformation"
      className="relative bg-[#1F2328] text-white py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        <div className="mb-16 md:mb-24 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#F5D77A]/80 mb-8">
              <span className="h-px w-8 bg-[#F5D77A]" />
              {copy.kicker}
            </div>
            <h2
              className="font-display font-light tracking-[-0.035em] leading-[0.95] text-white"
              style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
            >
              {copy.title}
            </h2>
            <p
              className="mt-8 md:mt-10 text-white/70 font-display font-light tracking-[-0.01em] leading-[1.2]"
              style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.6rem)" }}
            >
              {copy.sub}
            </p>
          </motion.div>
        </div>

        <div className="space-y-16 md:space-y-24">
          {PAIRS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`transformation-pair-${p.key}`}
            >
              <div className="flex items-baseline justify-between mb-4">
                <div className="text-[10px] tracking-[0.35em] uppercase text-white/45 font-display">
                  Perspektive · {String(i + 1).padStart(2, "0")}
                </div>
                <div className="text-[13px] text-white/60">{p.label}</div>
              </div>
              <BeforeAfterSlider
                before={p.before}
                after={p.after}
                afterFocus={p.afterFocus}
                upload={copy.upload}
                label={p.label}
                realized={p.realized}
              />
              {p.caption && (
                <p className="mt-4 text-[12px] tracking-[0.02em] text-white/55 max-w-2xl font-display italic">
                  {p.caption[lang]}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 md:mt-32 max-w-4xl"
          data-testid="transformation-tagline"
        >
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#F5D77A]/70 mb-6 font-display flex items-center gap-3">
            <span className="h-px w-8 bg-[#F5D77A]/60" />
            Perspektive · Morgen
          </div>
          <p
            className="font-display font-light tracking-[-0.025em] leading-[1.05] text-white/95"
            style={{ fontSize: "clamp(1.85rem, 4vw, 3.75rem)" }}
          >
            {copy.tagline}
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-10 text-[12px] text-white/45 italic max-w-2xl leading-relaxed border-l-2 border-[#F5D77A]/40 pl-5"
        >
          {copy.hint}
        </motion.p>
      </div>
    </section>
  );
}
